import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import '../../styles-wa/contact_selection_modal.css';

const ContactSelectionModal = ({
  isOpen,
  contacts = [],
  selectedContacts = new Set(),
  onSelectContact,
  onSelectAll,
  onClose,
  onSend,
  templateName,
  isSending = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter contacts based on search
  const filteredContacts = useMemo(() => {
    if (!searchQuery.trim()) return contacts;
    
    const query = searchQuery.toLowerCase();
    return contacts.filter(contact => {
      const name = (contact.name || '').toLowerCase();
      const phone = (contact.phone || contact.phone_number || '').toLowerCase();
      return name.includes(query) || phone.includes(query);
    });
  }, [contacts, searchQuery]);

  const isAllSelected = filteredContacts.length > 0 && 
    filteredContacts.every(contact => selectedContacts.has(contact.id));

  const toggleSelectAll = () => {
    if (isAllSelected) {
      // Deselect all filtered contacts
      filteredContacts.forEach(contact => {
        if (selectedContacts.has(contact.id)) {
          onSelectContact?.(contact.id);
        }
      });
    } else {
      // Select all filtered contacts
      filteredContacts.forEach(contact => {
        if (!selectedContacts.has(contact.id)) {
          onSelectContact?.(contact.id);
        }
      });
    }
  };

  const handleSend = () => {
    if (selectedContacts.size > 0) {
      onSend?.();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="wa-module">
      <div className="contact-selection-overlay">
      <div className="contact-selection-modal">
        {/* Header */}
        <div className="contact-selection-header">
          <div>
            <h2 className="contact-selection-title">Send Template</h2>
            <p className="contact-selection-subtitle">Select one or more contacts to send to</p>
          </div>
          <button
            className="contact-selection-close"
            onClick={onClose}
            title="Close"
            aria-label="Close contact selection"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="contact-selection-content">
          {/* Search Bar */}
          <div className="search-section">
            <div className="search-input-wrapper">
              <svg
                className="search-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                className="search-input"
                placeholder="Search by name or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchQuery && (
                <button
                  className="search-clear"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  ✖
                </button>
              )}
            </div>
          </div>

          {/* Selection Counter */}
          <div className="selection-counter">
            <span className="counter-label">
              Selected:
              <strong className="counter-value">{selectedContacts.size} Contact{selectedContacts.size !== 1 ? 's' : ''}</strong>
            </span>
            {filteredContacts.length > 0 && (
              <button
                className={`select-all-btn ${isAllSelected ? 'all-selected' : ''}`}
                onClick={toggleSelectAll}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span>{isAllSelected ? 'Deselect All' : 'Select All'}</span>
              </button>
            )}
          </div>

          {/* Contacts List */}
          <div className="contacts-list">
            {filteredContacts.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <p className="empty-title">No contacts found</p>
                <p className="empty-description">
                  {searchQuery ? 'Try adjusting your search query' : 'No contacts available'}
                </p>
              </div>
            ) : (
              filteredContacts.map((contact) => (
                <div
                  key={contact.id}
                  className={`contact-card ${selectedContacts.has(contact.id) ? 'selected' : ''}`}
                  onClick={() => onSelectContact?.(contact.id)}
                >
                  <div className="contact-checkbox">
                    <div className={`checkbox ${selectedContacts.has(contact.id) ? 'checked' : ''}`}>
                      {selectedContacts.has(contact.id) && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </div>
                  </div>

                  <div className="contact-info">
                    <div className="contact-avatar">
                      {contact.name ? contact.name.charAt(0).toUpperCase() : '?'}
                    </div>
                    <div className="contact-details">
                      <p className="contact-name">{contact.name || 'Unknown'}</p>
                      <p className="contact-phone">
                        📞 {contact.phone || contact.phone_number}
                      </p>
                      {contact.last_interaction && (
                        <p className="contact-meta">
                          Last interaction: {contact.last_interaction}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="contact-indicator">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer - Sticky */}
        <div className="contact-selection-footer">
          <button
            className="contact-btn-secondary"
            onClick={onClose}
            disabled={isSending}
          >
            Cancel
          </button>
          <button
            className="contact-btn-primary"
            onClick={handleSend}
            disabled={selectedContacts.size === 0 || isSending}
          >
            {isSending ? (
              <>
                <svg
                  className="btn-loader"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 12c0-4.4 3.6-8 8-8 3.3 0 6.2 2 7.4 5M22 12c0 4.4-3.6 8-8 8-3.3 0-6.2-2-7.4-5" />
                </svg>
                Sending...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                Send Template to {selectedContacts.size} Contact{selectedContacts.size !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
     </div>
    </div>,
    document.body
  );
};

export default ContactSelectionModal;



